<template>
	<div class="coupon-wallet-page">
		<div class="wallet-page-heading">
			<div>
				<h2>我的券包</h2>
				<p>查看并使用当前账号已领取的优惠券</p>
			</div>
			<div class="heading-decoration"><i class="el-icon-tickets"></i></div>
		</div>

		<div class="wallet-list-card">
			<tab-pane v-model="activeTab" :tabs="couponTabs" :pane-style="tabPaneStyle" @change="handleTabChange">
				<template slot="extra">
					<el-button class="coupon-rules-button" icon="el-icon-document" @click="goToCouponRules">
						优惠券使用规则说明
					</el-button>
				</template>

				<div v-loading="loading" class="wallet-result">
					<div v-if="coupons.length" class="wallet-grid">
						<wallet-coupon-card v-for="coupon in coupons" :key="coupon.id" :coupon="coupon" @use="useCoupon" />
					</div>
					<div v-else-if="!loading" class="wallet-empty">
						<i class="el-icon-tickets"></i>
						<strong>暂无{{ activeTabName }}优惠券</strong>
						<p>当前分类下还没有优惠券记录</p>
					</div>
					<jr-pagination
						v-if="pageOptions.total > pageOptions.limit"
						:limit="pageOptions.limit"
						:page.sync="pageOptions.page"
						:total="pageOptions.total"
						:page-sizes="[9, 18, 27]"
						layout="prev, pager, next"
						@change="handlePaginationChange"
					></jr-pagination>
				</div>
			</tab-pane>
		</div>
	</div>
</template>

<script>
	import TabPane from '@/components/TabPane'
	import WalletCouponCard from './components/WalletCouponCard'
	import { getMyCouponPage } from '@/api/coupon'

	export default {
		name: 'MyCoupon',
		components: { TabPane, WalletCouponCard },
		data() {
			return {
				activeTab: 'unused',
				loading: false,
				statusCounts: {
					unused: 0,
					used: 0,
					expired: 0
				},
				pageOptions: {
					page: 1,
					limit: 9,
					total: 0
				},
				tabPaneStyle: {
					'--tab-pane-header-padding-bottom': '12px',
					'--tab-pane-header-margin-bottom': '20px',
					'--tab-pane-item-font-size': '16px',
					'--tab-pane-indicator-bottom': '-12px'
				},
				couponSamples: [
					{
						id: 1,
						couponName: '新人咨询体验券',
						couponType: 30,
						couponTypeName: '体验券',
						faceValue: 1,
						discountCapAmount: 50,
						thresholdAmount: 200,
						applicableScopeName: '新人专享',
						issueStartTime: '2026-09-01',
						useEndTime: '2026-12-31',
						stackStrategy: 10,
						measureType: 10,
						couponStatus: 'unused'
					},
					{
						id: 2,
						couponName: '商城满减优惠券',
						couponType: 10,
						couponTypeName: '满减券',
						faceValue: 200,
						discountCapAmount: 200,
						thresholdAmount: 500,
						applicableScopeName: '指定商品',
						issueStartTime: '2026-09-01',
						useEndTime: '2026-12-31',
						stackStrategy: 10,
						measureType: null,
						couponStatus: 'unused'
					},
					{
						id: 3,
						couponName: '首单折扣券',
						couponType: 20,
						couponTypeName: '折扣券',
						faceValue: 8.8,
						discountCapAmount: 880,
						thresholdAmount: 0,
						applicableScopeName: '首单专享',
						issueStartTime: '2026-09-01',
						useEndTime: '2026-12-31',
						stackStrategy: 10,
						measureType: null,
						couponStatus: 'unused'
					},
					{
						id: 4,
						couponName: '企业服务满减券',
						couponType: 10,
						couponTypeName: '满减券',
						faceValue: 100,
						discountCapAmount: 100,
						thresholdAmount: 300,
						applicableScopeName: '指定类别',
						issueStartTime: '2026-07-01',
						useEndTime: '2026-10-31',
						stackStrategy: 20,
						measureType: null,
						couponStatus: 'used'
					},
					{
						id: 5,
						couponName: '会员专享折扣券',
						couponType: 20,
						couponTypeName: '折扣券',
						faceValue: 9,
						discountCapAmount: 500,
						thresholdAmount: 500,
						applicableScopeName: '会员专享',
						issueStartTime: '2026-07-01',
						useEndTime: '2026-10-31',
						stackStrategy: 30,
						measureType: null,
						couponStatus: 'used'
					},
					{
						id: 6,
						couponName: '夏季满减券',
						couponType: 10,
						couponTypeName: '满减券',
						faceValue: 50,
						discountCapAmount: 50,
						thresholdAmount: 200,
						applicableScopeName: '全场通用',
						issueStartTime: '2026-05-01',
						useEndTime: '2026-06-30',
						stackStrategy: 20,
						measureType: null,
						couponStatus: 'expired'
					},
					{
						id: 7,
						couponName: '资质咨询体验券',
						couponType: 30,
						couponTypeName: '体验券',
						faceValue: 7,
						discountCapAmount: 0,
						thresholdAmount: 0,
						applicableScopeName: '指定商品',
						issueStartTime: '2026-04-01',
						useEndTime: '2026-06-30',
						stackStrategy: 10,
						measureType: 20,
						couponStatus: 'expired'
					}
				],
				coupons: []
			}
		},
		computed: {
			couponTabs() {
				return [
					{ name: '未使用', value: 'unused', badge: this.statusCounts.unused },
					{ name: '已使用', value: 'used', badge: this.statusCounts.used },
					{ name: '已过期', value: 'expired', badge: this.statusCounts.expired }
				]
			},
			activeTabName() {
				const activeTab = this.couponTabs.find(item => item.value === this.activeTab)
				return activeTab ? activeTab.name : ''
			}
		},
		mounted() {
			this.loadCoupons()
		},
		methods: {
			async loadCoupons() {
				this.loading = true
				const statusMap = { unused: 10, used: 20, expired: 30 }
				try {
					const requests = Object.keys(statusMap).map(status =>
						getMyCouponPage({
							page: status === this.activeTab ? this.pageOptions.page : 1,
							size: status === this.activeTab ? this.pageOptions.limit : 1,
							status: statusMap[status]
						})
					)
					const responses = await Promise.all(requests)
					Object.keys(statusMap).forEach((status, index) => {
						const data = responses[index].data || {}
						this.statusCounts[status] = Number(data.total || 0)
						if (status === this.activeTab) {
							this.coupons = data.records || []
							this.pageOptions.total = Number(data.total || 0)
						}
					})
				} catch (error) {
					this.coupons = []
					this.pageOptions.total = 0
				} finally {
					this.loading = false
				}
			},
			handleTabChange() {
				this.pageOptions.page = 1
				this.loadCoupons()
			},
			handlePaginationChange(pager) {
				this.pageOptions.page = pager.page
				this.pageOptions.limit = pager.limit
				this.loadCoupons()
			},
			goToCouponRules() {
				this.$router.push({ name: 'CouponRules' })
			},
			useCoupon() {
				this.$router.push('/mallStore')
			}
		}
	}
</script>

<style lang="scss" scoped>
	.coupon-wallet-page {
		height: 100%;
		overflow-x: hidden;
		overflow-y: auto;
		font-size: 14px;
		background: #fff;
	}
	.wallet-page-heading {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 22px 26px;
		overflow: hidden;
		color: #fff;
		background: linear-gradient(118deg, #246fc8, #438ed8 60%, #66a8e2);
	}
	.wallet-page-heading h2 {
		margin: 0 0 7px;
		font-size: 22px;
	}
	.wallet-page-heading p {
		margin: 0;
		color: rgba(255, 255, 255, 0.8);
		font-size: 14px;
	}
	.heading-decoration {
		position: absolute;
		right: 32px;
		color: rgba(255, 255, 255, 0.18);
		font-size: 74px;
		transform: rotate(-8deg);
	}
	.wallet-list-card {
		padding: 22px 24px 28px;
		overflow: hidden;
	}
	.coupon-rules-button {
		padding: 8px 14px;
		border-color: #bed3e9;
		color: #3b73ad;
		background: #f7fbff;

		&:hover,
		&:focus {
			border-color: #2671cb;
			color: #2671cb;
			background: #edf5fd;
		}
	}
	.wallet-grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 28px 20px;
	}
	.wallet-result {
		min-height: 360px;
	}
	.wallet-empty {
		display: flex;
		min-height: 360px;
		align-items: center;
		flex-direction: column;
		justify-content: center;
		color: #9aa8b7;
	}
	.wallet-empty > i {
		margin-bottom: 18px;
		color: #c1d8ef;
		font-size: 64px;
	}
	.wallet-empty strong {
		color: #53677c;
		font-size: 18px;
	}
	.wallet-empty p {
		margin: 8px 0 0;
		font-size: 14px;
	}

	::v-deep {
		// 券卡悬浮时需要越过内容区边界展示位移和投影，仅在券包页放开通用 TabPane 的裁剪。
		.tab-pane-container .tab-pane-content {
			overflow: visible;
		}

		.tab-pane-container .tab-pane-header .tab-pane-navs .tab-pane-item {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			line-height: 1;
		}

		.tab-pane-container .tab-pane-header .tab-pane-navs .tab-pane-item .tab-pane-item-badge {
			position: static;
			top: auto;
			right: auto;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: 20px;
			height: 18px;
			margin-left: 7px;
			padding: 0 5px;
			box-sizing: border-box;
			border: 1px solid #d7e5f3;
			border-radius: 9px;
			color: #6883a0;
			background: #f1f6fb;
			font-size: 11px;
			font-weight: 500;
			line-height: 16px;
		}

		.tab-pane-container .tab-pane-header .tab-pane-navs .tab-pane-item.active .tab-pane-item-badge {
			border-color: #bfd7ef;
			color: #2671cb;
			background: #eaf4ff;
		}
	}
</style>
